import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQItem[] = [
  {
    category: 'General',
    question: 'What is Premium Visa Solutions?',
    answer: 'Premium Visa Solutions is a professional visa support and application assistance company based in Lusaka, Zambia. We help individuals, families, and organisations prepare and submit visa applications to various embassies and visa centres. We are not an embassy, consulate, or government authority.',
  },
  {
    category: 'General',
    question: 'Is Premium Visa Solutions an embassy or government office?',
    answer: 'No. Premium Visa Solutions is an independent private company. We are not an embassy, consulate, immigration authority, or government office. Final visa decisions are made only by the relevant embassy, consulate, or immigration department.',
  },
  {
    category: 'General',
    question: 'Where is your office located?',
    answer: 'Our office is located at Plot No. 1194, Corner of Lunzua Road / Addis Ababa Road, Lusaka, Zambia. You can visit us during business hours or use our Apply From Home service.',
  },
  {
    category: 'Services',
    question: 'What visa services do you offer?',
    answer: 'We offer a comprehensive range of visa support services including: visa application assistance, document review and verification, appointment booking, passport and document handling, Apply From Home service, document collection and delivery, application tracking, corporate and group visa support, and travel documentation support.',
  },
  {
    category: 'Services',
    question: 'Which countries do you assist with?',
    answer: 'We assist with visa applications for over 30 countries including UAE, Qatar, China, South Africa, Namibia, DRC, Egypt, Algeria, Zimbabwe, Malawi, Lebanon, Sri Lanka, Indonesia, Tanzania, Thailand, Saudi Arabia, Georgia, Bahrain, Guinea-Bissau, Madagascar, Uganda, Turkey, India, Senegal, Cyprus, Nigeria, and more.',
  },
  {
    category: 'Apply From Home',
    question: 'How does the Apply From Home service work?',
    answer: 'Simply fill out our online application form with your personal details, travel information, and upload your supporting documents. Our team reviews your application, contacts you to arrange document collection from your preferred location, and handles the entire visa process on your behalf.',
  },
  {
    category: 'Apply From Home',
    question: 'Do I need to visit your office?',
    answer: 'No. With our Apply From Home service, you can complete the entire visa application process without visiting our office. Our team comes to your home, office, or preferred location in Lusaka for document collection and delivery.',
  },
  {
    category: 'Apply From Home',
    question: 'Is the Apply From Home service available outside Lusaka?',
    answer: 'Currently, our document collection and delivery service is available within Lusaka. For clients outside Lusaka, please contact our team to discuss alternative arrangements.',
  },
  {
    category: 'Tracking',
    question: 'How can I track my visa application?',
    answer: 'After submitting your application, you will receive a unique PVS reference number (format: PVS-YYYY-XXXX). Enter this number on our Track Application page to see real-time progress of your application through all stages.',
  },
  {
    category: 'Tracking',
    question: 'How long does the visa process take?',
    answer: 'Processing times vary depending on the destination country, visa type, and embassy workload. Generally, visa processing can take anywhere from 3 to 30 working days. We provide estimated timelines during your application and keep you updated throughout.',
  },
  {
    category: 'Tracking',
    question: 'What do the different tracking stages mean?',
    answer: 'Our tracking system has 11 stages: Application Received, Documents Collected, Documents Under Review, Documents Verified, Appointment Scheduled, Submitted to Embassy, Embassy Processing, Additional Information Requested, Decision Received, Approved/Declined, and Passport Ready for Collection.',
  },
  {
    category: 'Documents',
    question: 'What documents do I need for a visa application?',
    answer: 'Required documents vary by country and visa type, but typically include: valid passport (minimum 6 months validity), passport-sized photos, bank statements, flight itinerary, hotel booking or invitation letter, travel insurance, and completed visa application forms. Our team will provide a detailed checklist for your specific application.',
  },
  {
    category: 'Documents',
    question: 'How do you handle my passport and documents?',
    answer: 'Your documents are handled with the highest level of security. We maintain a strict chain of custody from collection to delivery. All documents are stored in secure facilities, and we provide receipts and tracking at every stage.',
  },
  {
    category: 'Payments',
    question: 'How much do your services cost?',
    answer: 'Our service fees vary depending on the destination country, visa type, and services required. Please contact our team for a personalised quote. Note that our fees are separate from embassy visa fees.',
  },
  {
    category: 'Payments',
    question: 'What payment methods do you accept?',
    answer: 'We accept cash payments at our office, bank transfers, and mobile money transfers. Payment details will be provided when you submit your application.',
  },
  {
    category: 'Corporate',
    question: 'Do you handle corporate and group visa applications?',
    answer: 'Yes. We offer specialised corporate and group visa support for companies, NGOs, and organisations. We can handle bulk applications, business delegations, and provide dedicated account management for corporate clients.',
  },
  {
    category: 'Corporate',
    question: 'Can you assist with visa accreditation for our organisation?',
    answer: 'Yes. We assist organisations with visa accreditation registration at various embassies, which streamlines the visa application process for your staff and delegates.',
  },
  {
    category: 'Other',
    question: 'What happens if my visa is declined?',
    answer: 'While we work diligently to ensure your application meets all requirements, the final decision rests with the embassy. If your application is declined, our team will advise you on the reasons, available options, and assist with reapplication if appropriate.',
  },
  {
    category: 'Other',
    question: 'Can you guarantee that my visa will be approved?',
    answer: 'No. We cannot guarantee visa approval as the final decision is made solely by the relevant embassy or immigration authority. However, we ensure your application is complete, accurate, and meets all requirements to maximise your chances of approval.',
  },
  {
    category: 'Other',
    question: 'How do I contact Premium Visa Solutions?',
    answer: 'You can reach us by phone at +260 961 311 111, by email at info@pvs-zm.com, visit our office at Plot No. 1194, Corner of Lunzua Road / Addis Ababa Road, Lusaka, or use the contact form on our Contact Us page.',
  },
];

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...Array.from(new Set(faqs.map((f) => f.category)))];
  const filteredFaqs = activeCategory === 'All' ? faqs : faqs.filter((f) => f.category === activeCategory);

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20dark%20navy%20background%20with%20subtle%20question%20mark%20pattern%20in%20orange%20accents%2C%20modern%20help%20and%20support%20concept%2C%20professional%20corporate%20design&width=1920&height=600&seq=faqs-premium-visa&orientation=landscape"
          alt="Frequently Asked Questions"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="FAQs - Premium Visa Solutions"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Frequently Asked Questions</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[550px] mx-auto">
            Find answers to common questions about our visa support services
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[900px] mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
                className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            {filteredFaqs.map((faq, idx) => (
              <div key={idx} className="bg-background-50 border border-secondary-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-secondary-50 transition-colors"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                  <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openIndex === idx ? 'rotate-45' : ''}`}>
                    <i className={`text-lg ${openIndex === idx ? 'text-accent-500 ri-subtract-line' : 'text-foreground-400 ri-add-line'}`}></i>
                  </div>
                </button>
                {openIndex === idx && (
                  <div className="px-5 pb-4">
                    <div className="pt-1 border-t border-secondary-100">
                      <p className="text-sm text-foreground-600 leading-relaxed pt-3">{faq.answer}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-secondary-100">
        <div className="w-full max-w-[700px] mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Still Have Questions?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-6">
            Our team is ready to help. Contact us and we will respond within 24 hours.
          </p>
          <Link
            to="/contact-us"
            className="inline-block px-10 py-3.5 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer"
          >
            Contact Our Team
          </Link>
        </div>
      </section>
    </div>
  );
}